/**
 * @brisa/ui · facelift/footer/FooterFacelift
 *
 * Feature: migracao-facelift-v1 · COMPONENT_MAP §1 #7 (footer)
 * Figma origem: node 163:946 — reforma per 179:206 (Bruno 2026-05-14):
 *   - wrapper sage #ACB7A8 (toggle via withBackground prop) + inner card
 *     rgba(0,0,0,0.12) rounded-[32px]
 *   - layout 3 cols: Copyright (logotipo BrisaPass + ano) | Navigation
 *     (terciary buttons stacked) | Social (icon-buttons)
 * mobile-tweaks-v2 (Bruno 2026-05-19, Figma node 218:1145):
 *   - layout vertical 1 col em <md: Lista legal (com ArrowRight) → Divider
 *     → Social → Copyright (2 linhas)
 *   - ordem dos legais mantida igual ao desktop por decisão PL §7.6
 *     (Uso → Privacidade → Cookies — não alinha com Figma)
 *
 * NÃO substitui FooterLegacy em apps/web/components/ (dual strategy per
 * ADR §D8). Sem 'use client' — apresentacional puro (F1-FTR-02).
 */
import * as React from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';

import { cn } from '@brisa/ui/lib/utils';

export interface FooterLegalLink {
  href: string;
  label: string;
}

export interface FooterSocialLink {
  href: string;
  label: string;
  /** Quando true, abre em nova aba com rel noreferrer noopener (default true). */
  external?: boolean;
}

export interface FooterFaceliftProps extends React.HTMLAttributes<HTMLElement> {
  /** Quando true (default), renderiza o wrapper sage #ACB7A8. False = inner card direto. */
  withBackground?: boolean;
  /** Links legais à direita (default: termos/privacidade/cookies). */
  legalLinks?: FooterLegalLink[];
  /** Links sociais (default: instagram + linkedin placeholders). */
  socialLinks?: FooterSocialLink[];
}

const DEFAULT_LEGAL_LINKS: FooterLegalLink[] = [
  { href: '/termos', label: 'Termos de uso' },
  { href: '/privacidade', label: 'Termos de privacidade' },
  { href: '/cookies', label: 'Política de Cookies' },
];

const InstagramIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
    width={16}
    height={16}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="2" y="2" width="12" height="12" rx="3.5" />
    <circle cx="8" cy="8" r="2.5" />
    <circle cx="11.5" cy="4.5" r="0.4" fill="currentColor" />
  </svg>
);

const LinkedinIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
    width={16}
    height={16}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10.667 6a3.333 3.333 0 0 1 3.333 3.333V14h-2.667V9.333a1 1 0 0 0-2 0V14H6.667V9.333A3.333 3.333 0 0 1 10 6z" />
    <rect x="1.5" y="6.5" width="2.667" height="7.5" />
    <circle cx="2.833" cy="3.167" r="1.333" />
  </svg>
);

const DEFAULT_SOCIAL_LINKS: FooterSocialLink[] = [
  { href: 'https://instagram.com', label: 'Instagram', external: true },
  { href: 'https://linkedin.com', label: 'LinkedIn', external: true },
];

const SOCIAL_ICONS: Record<string, React.ReactNode> = {
  Instagram: <InstagramIcon />,
  LinkedIn: <LinkedinIcon />,
};

function SocialIconsRow({
  socialLinks,
  className,
}: {
  socialLinks: FooterSocialLink[];
  className?: string;
}) {
  return (
    <div className={cn('flex items-center gap-2', className)} data-slot="social">
      {socialLinks.map((link) => {
        const icon = SOCIAL_ICONS[link.label] ?? null;
        const isExternal = link.external !== false;
        return (
          <a
            key={link.href}
            href={link.href}
            aria-label={link.label}
            target={isExternal ? '_blank' : undefined}
            rel={isExternal ? 'noreferrer noopener' : undefined}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[hsl(var(--text-100))] transition-colors duration-fast hover:bg-[hsl(var(--text-08))]"
          >
            {icon}
          </a>
        );
      })}
    </div>
  );
}

function CopyrightBlock({ year, className }: { year: number; className?: string }) {
  return (
    <div
      className={cn('flex flex-col items-start gap-12 self-stretch text-left', className)}
      data-slot="copyright"
    >
      <span
        className="font-body text-xl font-black leading-none tracking-[-0.04em] text-[hsl(var(--text-100))]"
        aria-label="Brisa Pass"
      >
        BrisaPass
      </span>
      <span className="font-body text-xs leading-none text-[hsl(var(--text-56))]">
        © {year} . Todos os direitos reservados.
      </span>
    </div>
  );
}

export function FooterFacelift({
  withBackground = true,
  legalLinks = DEFAULT_LEGAL_LINKS,
  socialLinks = DEFAULT_SOCIAL_LINKS,
  className,
  ...props
}: FooterFaceliftProps) {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      className={cn(
        // Padding externo (p-8) preservado em ambas variantes — só o bg sage
        // que toggla. Bruno 2026-05-15: flat == transparent + padding mantido.
        'w-full px-4 py-8 sm:px-8',
        withBackground ? 'bg-[#ACB7A8]' : 'bg-transparent',
        className,
      )}
      data-slot="footer"
      data-variant={withBackground ? 'boxed' : 'flat'}
      {...props}
    >
      <div className="mx-auto w-full max-w-[1440px]" data-slot="footer-max-width">
        {/* Mobile layout — Figma node 218:1145 (mobile-tweaks-v2).
            Visível em <md. Ordem vertical: Lista legal (com ArrowRight) →
            Divider → Social → Copyright (2 linhas). */}
        <div
          className="flex w-full flex-col gap-6 rounded-[32px] bg-[rgba(0,0,0,0.12)] p-8 md:hidden"
          data-slot="footer-inner-mobile"
        >
          <nav aria-label="Links legais" className="flex flex-col" data-slot="navigation-mobile">
            {legalLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="flex items-center justify-between py-4 font-body text-sm font-semibold leading-none text-[hsl(var(--text-100))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <span>{link.label}</span>
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            ))}
          </nav>

          <div className="h-px w-full bg-[rgba(0,0,0,0.04)]" data-slot="divider-mobile" />

          <SocialIconsRow socialLinks={socialLinks} />

          <CopyrightBlock year={currentYear} className="!gap-2" />
        </div>

        {/* Desktop layout — preservado per ADR migracao-facelift-v1 §D? +
            Bruno 2026-05-15. Visível em md+. */}
        <div
          className="hidden w-full items-start justify-between gap-8 rounded-[var(--radius-xl)] bg-[rgba(0,0,0,0.12)] p-8 md:flex"
          data-slot="footer-inner"
        >
          <CopyrightBlock year={currentYear} />

          {/* Right cluster — legalLinks + social agrupados na direita com 72px de gap
              (Bruno 2026-05-15) — links de termos puxados pra perto dos icons sociais. */}
          <div className="flex items-center gap-[72px]" data-slot="right-cluster">
            <nav
              aria-label="Links legais"
              className="flex flex-col items-end gap-[14px]"
              data-slot="navigation"
            >
              {legalLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="font-body text-sm font-semibold leading-none text-[hsl(var(--text-100))] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <SocialIconsRow socialLinks={socialLinks} />
          </div>
        </div>
      </div>
    </footer>
  );
}
